import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  ackPunchNotesReview,
  getFieldNoteCount,
  getFieldNotes,
  getPunchNotesReview,
  getPunchNotesReviewCount,
  getUsers,
  processFieldNote,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useDashboardRoles } from "@/pages/dashboard/useDashboardRoles";

export function useAdminFieldNotesViewModel() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { canViewFieldNotesAdmin, canReviewFieldNotes, isDualManagerReadonly, managedEditUserIds, isAdmin, isManagerOnly, isReadonlyAdmin } = useDashboardRoles();
  const online = useOnlineStatus();

  const [fieldNotesAckFilter, setFieldNotesAckFilter] = React.useState<"unacknowledged" | "all">("unacknowledged");
  const [fieldNotesEmployeeFilter, setFieldNotesEmployeeFilter] = React.useState<string>("all");
  const [fieldNotesEmployeeSearch, setFieldNotesEmployeeSearch] = React.useState("");
  const [fieldNotesEmployeeOpen, setFieldNotesEmployeeOpen] = React.useState(false);
  const fieldNotesEmployeeRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (fieldNotesEmployeeRef.current && !fieldNotesEmployeeRef.current.contains(event.target as Node)) {
        setFieldNotesEmployeeOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  /** Keep selected employee label after search results change. */
  const [fieldNotesEmployeeLabel, setFieldNotesEmployeeLabel] = React.useState<string>("");

  const { data: fieldNotesEmployeesData } = useQuery({
    queryKey: ["users-filter-field-notes", fieldNotesEmployeeSearch],
    queryFn: () =>
      getUsers({
        include_inactive: true,
        search: fieldNotesEmployeeSearch.trim() || undefined,
        limit: 100,
        offset: 0,
      }),
    enabled: !!user && canViewFieldNotesAdmin,
  });

  const {
    data: fieldNotesReviewData,
    isLoading: fieldNotesReviewLoading,
    isError: fieldNotesReviewError,
    error: fieldNotesReviewErr,
    refetch: refetchFieldNotesReview,
  } = useQuery({
    queryKey: ["field-notes-review", fieldNotesAckFilter, fieldNotesEmployeeFilter],
    queryFn: () =>
      getFieldNotes({
        limit: 200,
        all: true,
        processed: fieldNotesAckFilter === "unacknowledged" ? false : undefined,
        user_id: fieldNotesEmployeeFilter !== "all" ? Number(fieldNotesEmployeeFilter) : undefined,
      }),
    enabled: !!user && canViewFieldNotesAdmin,
  });

  const { data: unprocessedFieldNotesCountData } = useQuery({
    queryKey: ["field-notes-count", "unprocessed"],
    queryFn: () => getFieldNoteCount({ processed: false, all: true }),
    enabled: !!user && canViewFieldNotesAdmin,
  });

  const {
    data: punchNotesReviewData,
    isLoading: punchNotesReviewLoading,
    isError: punchNotesReviewError,
    error: punchNotesReviewErr,
    refetch: refetchPunchNotesReview,
  } = useQuery({
    queryKey: ["punch-notes-review", fieldNotesEmployeeFilter],
    queryFn: () =>
      getPunchNotesReview({
        user_id: fieldNotesEmployeeFilter !== "all" ? Number(fieldNotesEmployeeFilter) : undefined,
        limit: 200,
      }),
    enabled: !!user && canViewFieldNotesAdmin,
  });

  const { data: punchNotesReviewCountData } = useQuery({
    queryKey: ["punch-notes-review-count"],
    queryFn: () => getPunchNotesReviewCount(),
    enabled: !!user && canViewFieldNotesAdmin,
  });
  const unackedNotesTotal =
    (unprocessedFieldNotesCountData?.count ?? 0) + (punchNotesReviewCountData?.count ?? 0);


  // createFieldNoteMutation replaced by offline enqueue for standalone notes (see button below).
  // Direct path removed so notes are durably queued in IndexedDB like punches.

  const [fieldNoteProcessError, setFieldNoteProcessError] = React.useState<string | null>(null);
  const processFieldNoteMutation = useMutation({
    mutationFn: (id: number) => processFieldNote(id),
    onSuccess: async () => {
      setFieldNoteProcessError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["field-notes"] }),
        queryClient.invalidateQueries({ queryKey: ["field-notes-count"] }),
        queryClient.invalidateQueries({ queryKey: ["field-notes-review"] }),
        queryClient.invalidateQueries({ queryKey: ["punch-notes-review"] }),
        queryClient.invalidateQueries({ queryKey: ["punch-notes-review-count"] }),
      ]);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Could not acknowledge field note.";
      setFieldNoteProcessError(msg);
    },
  });

  const ackPunchNotesReviewMutation = useMutation({
    mutationFn: (entryId: number) => ackPunchNotesReview(entryId),
    onSuccess: async () => {
      setFieldNoteProcessError(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["field-notes"] }),
        queryClient.invalidateQueries({ queryKey: ["field-notes-count"] }),
        queryClient.invalidateQueries({ queryKey: ["field-notes-review"] }),
        queryClient.invalidateQueries({ queryKey: ["punch-notes-review"] }),
        queryClient.invalidateQueries({ queryKey: ["punch-notes-review-count"] }),
      ]);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Could not acknowledge punch notes.";
      setFieldNoteProcessError(msg);
    },
  });



  return {
    user,
    online,
    canViewFieldNotesAdmin,
    canReviewFieldNotes,
    isDualManagerReadonly,
    managedEditUserIds,
    isAdmin,
    isManagerOnly,
    isReadonlyAdmin,
    fieldNotesAckFilter, setFieldNotesAckFilter,
    fieldNotesEmployeeFilter, setFieldNotesEmployeeFilter,
    fieldNotesEmployeeSearch, setFieldNotesEmployeeSearch,
    fieldNotesEmployeeOpen, setFieldNotesEmployeeOpen,
    fieldNotesEmployeeRef,
    fieldNotesEmployeeLabel, setFieldNotesEmployeeLabel,
    fieldNotesEmployeesData,
    fieldNotesReviewData, fieldNotesReviewLoading, fieldNotesReviewError, fieldNotesReviewErr, refetchFieldNotesReview,
    punchNotesReviewData, punchNotesReviewLoading, punchNotesReviewError, punchNotesReviewErr, refetchPunchNotesReview,
    unackedNotesTotal,
    fieldNoteProcessError,
    processFieldNoteMutation,
    ackPunchNotesReviewMutation,
  };
}
